import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, forwardRef, Input, OnInit } from '@angular/core';
import { LetDirective } from '@ngrx/component';
import { Observable } from 'rxjs';

import { LazyComponentId } from 'src/app/core/services/lazy-components/lazy-components-registry';

import { TrackingDirective } from '../../core/services/tracking/directive/tracking.directive';
import { FootnoteHintComponent } from '../footnotes/footnote-hint/footnote-hint.component';
import { ModalService } from '../modal/modal-service/modal.service';
import { RETURN_TRANSLATION_OR_KEY, TranslatePipe } from '../pipes/translate/translate.pipe';
import { WishlistSaveButtonComponent } from '../wishlist-save-button/wishlist-save-button.component';

import { NbaEvent, NbaSecondaryItemComponent } from './nba-secondary-item/nba-secondary-item.component';
import { NbaConfig, NbaPosition, Nbas } from './nba.model';
import { NbaService } from './nba.service';
import { NbaConditionResolversModule } from './resolvers/condition/nba-condition-resolvers.module';
import { NbaParamResolversModule } from './resolvers/param/nba-param-resolvers.module';

/**
 * Icon widget enums Values need to match to the config (NBA config).
 */
export enum CustomWidget {
  WishlistSaveButton = 'wishlistSaveButton'
}

@Component({
  selector: 'cc-nba-bar',
  templateUrl: './nba-bar.component.html',
  styleUrl: './nba-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    LetDirective,
    NgTemplateOutlet,
    TrackingDirective,
    TranslatePipe,
    NbaSecondaryItemComponent,
    WishlistSaveButtonComponent,
    // TODO: Fix with MBCC-2650
    // remove forwardRef when underlying issue is fixed
    // eslint-disable-next-line @angular-eslint/no-forward-ref
    forwardRef(() => FootnoteHintComponent),
    AsyncPipe,
    // needed as its (dynamically configured) NBAs
    NbaParamResolversModule,
    NbaConditionResolversModule
  ]
})
export class NbaBarComponent implements OnInit {
  @Input() public position: NbaPosition = 'summary-vehicle-data';

  public nbas$!: Observable<Nbas>;

  public readonly customWidgetEnum = CustomWidget;

  public returnTranslationOrKey = RETURN_TRANSLATION_OR_KEY;

  public constructor(
    private readonly nbaService: NbaService,
    private readonly modalService: ModalService
  ) {}

  public ngOnInit(): void {
    // Because changes outside our app can reload the configuration and affect the buttons, we need to monitor the NBA bar value.
    this.nbas$ = this.nbaService.initNbaBarData(this.position);
  }

  public onPrimaryNbaClick(event: NbaEvent): void {
    this.handleClick(event.item as unknown as NbaConfig);
  }

  public onSecondaryNbaPopoverMainItemClick(event: NbaEvent): void {
    this.handleClick(event.item as unknown as NbaConfig);
  }

  public onSecondaryNbaPopoverSubItemClick(event: NbaEvent): void {
    this.handleClick(event.item as unknown as NbaConfig);
  }

  /**
   * Only available for temporary AB testing (B2O)!
   */
  public openBuildToOrderModal(): void {
    this.modalService.open(LazyComponentId.BuildToOrder);
  }

  private handleClick(nba: NbaConfig): void {
    this.nbaService.onNbaClick(nba);
  }
}
